import { useEffect, useMemo, useState } from 'react';
import {
  listDevices,
  recentViews,
  type DeviceListItem,
  type RecentViewsResp,
} from '../api';

const LS_PREFIX_KEY = 'dashboard.prefix';

type Status = 'green' | 'yellow' | 'red' | 'gray';

interface DeviceRow {
  name: string;
  count1h: number;
  count3h: number;
  count24h: number;
  status: Status;
  /** network → count within last 24h */
  byNetwork: Map<string, number>;
}

/** ネットワークごとの固定色 (未登録は palette から採番)。 */
const NET_COLORS: Record<string, string> = {
  line: '#7ED321',
  applovin: '#F5A623',
  admob: '#4A90E2',
};
const NET_PALETTE = [
  '#008FFB',
  '#00E396',
  '#FEB019',
  '#FF4560',
  '#775DD0',
  '#3F51B5',
  '#546E7A',
  '#D4526E',
  '#8D5B4C',
  '#F86624',
];
const networkColorMap = new Map<string, string>();
function networkColor(name: string): string {
  const key = name.toLowerCase();
  if (NET_COLORS[key]) return NET_COLORS[key];
  const cached = networkColorMap.get(key);
  if (cached) return cached;
  const c = NET_PALETTE[networkColorMap.size % NET_PALETTE.length];
  networkColorMap.set(key, c);
  return c;
}

function statusOf(row: {
  count1h: number;
  count3h: number;
  count24h: number;
}): Status {
  if (row.count1h > 0) return 'green';
  if (row.count3h > 0) return 'yellow';
  if (row.count24h > 0) return 'red';
  return 'gray';
}

export default function Dashboard() {
  const [prefix, setPrefix] = useState<string>(() => {
    try {
      return localStorage.getItem(LS_PREFIX_KEY) ?? '';
    } catch {
      return '';
    }
  });
  const [devices, setDevices] = useState<DeviceListItem[]>([]);
  const [d1h, setD1h] = useState<RecentViewsResp | null>(null);
  const [d3h, setD3h] = useState<RecentViewsResp | null>(null);
  const [d24h, setD24h] = useState<RecentViewsResp | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(LS_PREFIX_KEY, prefix);
    } catch {
      // ignore
    }
  }, [prefix]);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const [dv, r1, r3, r24] = await Promise.all([
        listDevices(),
        recentViews(60),
        recentViews(180),
        recentViews(1440),
      ]);
      setDevices(dv.devices);
      setD1h(r1);
      setD3h(r3);
      setD24h(r24);
    } catch (e) {
      setErr(String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    const iv = setInterval(() => void load(), 30_000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows: DeviceRow[] = useMemo(() => {
    // device 名 → 各 window 別合計 + network 別 breakdown
    const sum1h = new Map<string, number>();
    const sum3h = new Map<string, number>();
    const sum24h = new Map<string, number>();
    const byNet = new Map<string, Map<string, number>>();
    for (const it of d1h?.items ?? []) {
      sum1h.set(it.device, (sum1h.get(it.device) || 0) + it.count);
    }
    for (const it of d3h?.items ?? []) {
      sum3h.set(it.device, (sum3h.get(it.device) || 0) + it.count);
    }
    for (const it of d24h?.items ?? []) {
      sum24h.set(it.device, (sum24h.get(it.device) || 0) + it.count);
      const m = byNet.get(it.device) ?? new Map<string, number>();
      m.set(it.network, (m.get(it.network) || 0) + it.count);
      byNet.set(it.device, m);
    }

    // 非表示フラグ (テスト端末や動いてない端末を雑音から除外)
    const hiddenSet = new Set<string>(
      devices.filter((d) => d.hidden === true).map((d) => d.name),
    );
    const nameSet = new Set<string>(
      devices.filter((d) => d.hidden !== true).map((d) => d.name),
    );
    // devices に載ってない (=登録のない) 名前も view には出るので拾う。
    // ただし hidden 指定があるものは除外。
    for (const n of sum24h.keys()) if (!hiddenSet.has(n)) nameSet.add(n);
    for (const n of sum1h.keys()) if (!hiddenSet.has(n)) nameSet.add(n);
    for (const n of sum3h.keys()) if (!hiddenSet.has(n)) nameSet.add(n);

    const list: DeviceRow[] = [];
    const p = prefix.trim().toLowerCase();
    for (const name of nameSet) {
      if (p && !name.toLowerCase().startsWith(p)) continue;
      const row: DeviceRow = {
        name,
        count1h: sum1h.get(name) || 0,
        count3h: sum3h.get(name) || 0,
        count24h: sum24h.get(name) || 0,
        status: 'gray',
        byNetwork: byNet.get(name) ?? new Map(),
      };
      row.status = statusOf(row);
      list.push(row);
    }
    // green→yellow→red→gray、同ステータス内は名前順
    const rank: Record<Status, number> = { green: 0, yellow: 1, red: 2, gray: 3 };
    list.sort(
      (a, b) => rank[a.status] - rank[b.status] || a.name.localeCompare(b.name),
    );
    return list;
  }, [devices, d1h, d3h, d24h, prefix]);

  const counts = useMemo(() => {
    const c = { green: 0, yellow: 0, red: 0, gray: 0 };
    for (const r of rows) c[r.status]++;
    return c;
  }, [rows]);

  const totals = useMemo(() => {
    let h1 = 0,
      h3 = 0,
      h24 = 0;
    for (const r of rows) {
      h1 += r.count1h;
      h3 += r.count3h;
      h24 += r.count24h;
    }
    return { h1, h3, h24 };
  }, [rows]);

  return (
    <div>
      <h2>ダッシュボード</h2>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="row" style={{ gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <label htmlFor="prefix-search">端末プレフィックス:</label>
          <input
            id="prefix-search"
            type="text"
            value={prefix}
            onChange={(e) => setPrefix(e.target.value)}
            placeholder="例: aihara / 空欄で全端末"
            style={{ minWidth: 200 }}
          />
          <button className="ghost small" onClick={() => void load()}>
            リロード
          </button>
          {loading && <span className="muted">読み込み中…</span>}
          {err && <span style={{ color: 'var(--bad)' }}>{err}</span>}
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: 8,
            marginTop: 12,
          }}
        >
          <StatusCount label="ライブ" n={counts.green} color="var(--good)" />
          <StatusCount label="アラート" n={counts.yellow} color="var(--warn)" />
          <StatusCount label="デッド" n={counts.red} color="var(--bad)" />
          <StatusCount label="24h ゼロ" n={counts.gray} color="var(--muted)" />
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 8,
            marginTop: 8,
          }}
        >
          <StatusCount label="1h 合計" n={totals.h1} color="var(--accent)" />
          <StatusCount label="3h 合計" n={totals.h3} color="var(--accent)" />
          <StatusCount label="24h 合計" n={totals.h24} color="var(--accent)" />
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="muted">該当する端末がありません</p>
      ) : (
        <>
          <NetworkLegend rows={rows} />
          <div className="status-grid">
            {rows.map((r) => (
              <DeviceTile key={r.name} row={r} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function NetworkLegend({ rows }: { rows: DeviceRow[] }) {
  // 全 tile に登場したネットワークを集めて凡例に。
  const set = new Set<string>();
  for (const r of rows) for (const n of r.byNetwork.keys()) set.add(n);
  const nets = [...set].sort();
  if (nets.length === 0) return null;
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 12,
        margin: '4px 0 10px',
        color: 'var(--muted)',
        fontSize: 12,
      }}
    >
      <span>広告先:</span>
      {nets.map((n) => (
        <span key={n} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              display: 'inline-block',
              width: 14,
              height: 8,
              background: networkColor(n),
              borderRadius: 2,
            }}
          />
          <span style={{ color: 'var(--text)' }}>{n}</span>
        </span>
      ))}
    </div>
  );
}

function StatusCount({
  label,
  n,
  color,
}: {
  label: string;
  n: number;
  color: string;
}) {
  return (
    <div
      style={{
        border: `1px solid ${color}`,
        background: `${color}22`,
        borderRadius: 6,
        padding: '10px 12px',
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: 12, color: 'var(--muted)' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700 }}>{n}</div>
    </div>
  );
}

function DeviceTile({ row }: { row: DeviceRow }) {
  const bg =
    row.status === 'green'
      ? 'rgba(74, 222, 128, 0.18)'
      : row.status === 'yellow'
        ? 'rgba(251, 191, 36, 0.18)'
        : row.status === 'red'
          ? 'rgba(248, 113, 113, 0.18)'
          : 'rgba(148, 163, 184, 0.08)';
  const border =
    row.status === 'green'
      ? 'var(--good)'
      : row.status === 'yellow'
        ? 'var(--warn)'
        : row.status === 'red'
          ? 'var(--bad)'
          : 'var(--border)';

  // stacked bar segments
  const nets = [...row.byNetwork.entries()].sort((a, b) => b[1] - a[1]);
  const segments = nets.map(([net, c]) => ({
    net,
    c,
    ratio: row.count24h > 0 ? (c / row.count24h) * 100 : 0,
  }));

  return (
    <div
      className="device-tile"
      style={{
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: 6,
        padding: 8,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        minHeight: 78,
        minWidth: 0,
        overflow: 'hidden',
      }}
      title={`1h: ${row.count1h}  3h: ${row.count3h}  24h: ${row.count24h}`}
    >
      <div className="tile-name">{row.name}</div>
      <div className="tile-counts">
        {row.count1h} / {row.count3h} / {row.count24h}
      </div>
      <div
        style={{
          height: 8,
          background: 'rgba(255,255,255,0.06)',
          borderRadius: 3,
          display: 'flex',
          overflow: 'hidden',
          marginTop: 'auto',
        }}
      >
        {segments.map((s) => (
          <div
            key={s.net}
            style={{
              height: '100%',
              width: `${s.ratio}%`,
              background: networkColor(s.net),
              minWidth: s.ratio > 0 ? 2 : 0,
            }}
            title={`${s.net}: ${s.c}`}
          />
        ))}
      </div>
    </div>
  );
}
